import './Itinerary.scss'
import Navigation from '../components/Navigation'
import { useState } from 'react'

interface ItineraryLeg {
  date: string
  departure: {
    city: string
    airport: string
    code?: string
  }
  arrival: {
    city: string
    airport: string
    code?: string
  }
  distance_nm: number
  overnight?: boolean
}

interface Tour1925 {
  name: string
  dates: string
  distance_nm: number
  stops: number
  aircraft: string
  focus: string
  sponsors: string
  notable_aircraft: string[]
  itinerary: ItineraryLeg[]
}

interface Tour2025 {
  name: string
  dates: string
  distance_nm: number
  stops: number
  overnight_stops: string[]
  focus: string
  notable_aircraft: string[]
  itinerary: ItineraryLeg[]
}

const Itinerary = () => {
  const [selectedYear, setSelectedYear] = useState<'1925' | '2025'>('1925')

  const tourData: { '1925': Tour1925; '2025': Tour2025 } = {
    "1925": {
      "name": "Ford National Reliability Air Tour",
      "dates": "September 28 – October 4, 1925",
      "distance_nm": 1543,
      "stops": 13,
      "aircraft": "17 airplanes from 11 manufacturers",
      "focus": "Reliability, safety, public confidence in air travel",
      "sponsors": "Henry & Edsel Ford, Ford Motor Company",
      "notable_aircraft": [
        "Fokker F.VII Tri-Motor",
        "Travel Air B.6",
        "Junkers F.13L",
        "Curtiss Carrier Pigeon",
        "Martin Model 70",
        "Swallow '25",
        "Waco 9"
      ],
      "itinerary": [
        { 
          "date": "September 28", 
          "departure": { "city": "Dearborn", "airport": "Ford Airport" },
          "arrival": { "city": "Chicago", "airport": "Maywood Field" },
          "distance_nm": 243 
        },
        { 
          "date": "September 29", 
          "departure": { "city": "Chicago", "airport": "Maywood Field" },
          "arrival": { "city": "Omaha", "airport": "Fort Crook Field" },
          "distance_nm": 374 
        },
        { 
          "date": "September 30", 
          "departure": { "city": "Omaha", "airport": "Fort Crook Field" },
          "arrival": { "city": "Kansas City", "airport": "Richards Field" },
          "distance_nm": 148 
        },
        { 
          "date": "October 1", 
          "departure": { "city": "Kansas City", "airport": "Richards Field" },
          "arrival": { "city": "St. Louis", "airport": "Lambert Field" },
          "distance_nm": 204 
        },
        { 
          "date": "October 2", 
          "departure": { "city": "St. Louis", "airport": "Lambert Field" },
          "arrival": { "city": "Indianapolis", "airport": "Schoen Field" },
          "distance_nm": 204 
        },
        { 
          "date": "October 3", 
          "departure": { "city": "Indianapolis", "airport": "Schoen Field" },
          "arrival": { "city": "Columbus", "airport": "Norton Field" },
          "distance_nm": 148 
        },
        { 
          "date": "October 4", 
          "departure": { "city": "Columbus", "airport": "Norton Field" },
          "arrival": { "city": "Dearborn", "airport": "Ford Airport" },
          "distance_nm": 222 
        }
      ]
    },
    "2025": {
      "name": "Commemorative Air Tour",
      "dates": "September 25 – September 28, 2025",
      "distance_nm": 1477,
      "stops": 17,
      "overnight_stops": ["Kansas City", "St. Louis", "Indianapolis", "Detroit", "Chicago"],
      "focus": "Recreation of 1925 tour, honoring legacy",
      "notable_aircraft": [
        "Mooney M20K Encore",
        "Mooney M20K 252",
        "Mooney M20M TLS Bravo"
      ],
      "itinerary": [
        { 
          "date": "September 25", 
          "departure": { "city": "Kansas City", "airport": "Charles B. Wheeler Downtown Airport", "code": "KMKC" },
          "arrival": { "city": "St. Louis", "airport": "Spirit of St. Louis Airport", "code": "KSUS" },
          "distance_nm": 187,
          "overnight": false
        },
        { 
          "date": "September 25", 
          "departure": { "city": "St. Louis", "airport": "Spirit of St. Louis Airport", "code": "KSUS" },
          "arrival": { "city": "Terre Haute", "airport": "Hulman Regional Airport", "code": "KHUF" },
          "distance_nm": 163,
          "overnight": false
        },
        { 
          "date": "September 25", 
          "departure": { "city": "Terre Haute", "airport": "Hulman Regional Airport", "code": "KHUF" },
          "arrival": { "city": "Indianapolis", "airport": "Eagle Creek Airpark", "code": "KEYE" },
          "distance_nm": 52,
          "overnight": true
        },
        { 
          "date": "September 26", 
          "departure": { "city": "Indianapolis", "airport": "Eagle Creek Airpark", "code": "KEYE" },
          "arrival": { "city": "Dayton", "airport": "Wright Brothers Airport", "code": "KMGY" },
          "distance_nm": 97,
          "overnight": false
        },
        { 
          "date": "September 26", 
          "departure": { "city": "Dayton", "airport": "Wright Brothers Airport", "code": "KMGY" },
          "arrival": { "city": "Columbus", "airport": "Ohio State University Airport", "code": "KOSU" },
          "distance_nm": 61,
          "overnight": true
        },
        { 
          "date": "September 26", 
          "departure": { "city": "Columbus", "airport": "Ohio State University Airport", "code": "KOSU" },
          "arrival": { "city": "Cleveland", "airport": "Burke Lakefront Airport", "code": "KBKL" },
          "distance_nm": 107,
          "overnight": false
        },
        { 
          "date": "September 26", 
          "departure": { "city": "Cleveland", "airport": "Burke Lakefront Airport", "code": "KBKL" },
          "arrival": { "city": "Detroit", "airport": "Ann Arbor Municipal Airport", "code": "KARB" },
          "distance_nm": 102,
          "overnight": true
        },
        { 
          "date": "September 27", 
          "departure": { "city": "Detroit", "airport": "Ann Arbor Municipal Airport", "code": "KARB" },
          "arrival": { "city": "Fort Wayne", "airport": "Smith Field", "code": "KSMD" },
          "distance_nm": 91,
          "overnight": false
        },
        { 
          "date": "September 27", 
          "departure": { "city": "Fort Wayne", "airport": "Smith Field", "code": "KSMD" },
          "arrival": { "city": "Chicago", "airport": "Chicago Midway International Airport", "code": "KMDW" },
          "distance_nm": 123,
          "overnight": true
        },
        { 
          "date": "September 28", 
          "departure": { "city": "Chicago", "airport": "Chicago Midway International Airport", "code": "KMDW" },
          "arrival": { "city": "Moline", "airport": "Davenport Municipal Airport", "code": "KDVN" },
          "distance_nm": 128,
          "overnight": false
        },
        { 
          "date": "September 28", 
          "departure": { "city": "Moline", "airport": "Davenport Municipal Airport", "code": "KDVN" },
          "arrival": { "city": "Des Moines", "airport": "Iowa Speedway Airport", "code": "KIKV" },
          "distance_nm": 134,
          "overnight": false
        },
        { 
          "date": "September 28", 
          "departure": { "city": "Des Moines", "airport": "Iowa Speedway Airport", "code": "KIKV" },
          "arrival": { "city": "Omaha", "airport": "Council Bluffs Municipal Airport", "code": "KCBF" },
          "distance_nm": 102,
          "overnight": false
        },
        { 
          "date": "September 28", 
          "departure": { "city": "Omaha", "airport": "Council Bluffs Municipal Airport", "code": "KCBF" },
          "arrival": { "city": "St. Joseph", "airport": "Rosecrans Memorial Airport", "code": "KSTJ" },
          "distance_nm": 97,
          "overnight": false
        },
        { 
          "date": "September 28", 
          "departure": { "city": "St. Joseph", "airport": "Rosecrans Memorial Airport", "code": "KSTJ" },
          "arrival": { "city": "Kansas City", "airport": "Charles B. Wheeler Downtown Airport", "code": "KMKC" },
          "distance_nm": 41,
          "overnight": false
        }
      ]
    }
  }

  const currentTour = tourData[selectedYear]

  return (
    <div className="itinerary-page">
      <Navigation />
      <div className="itinerary-container">
        <div className="year-selector desktop-selector">
          <button 
            className={`year-btn ${selectedYear === '1925' ? 'active' : ''}`}
            onClick={() => setSelectedYear('1925')}
          >
            1925 Original
          </button>
          <button 
            className={`year-btn ${selectedYear === '2025' ? 'active' : ''}`}
            onClick={() => setSelectedYear('2025')}
          >
            2025 Commemorative
          </button>
        </div>

        <h1 className="itinerary-title">{currentTour.name}</h1>
        
        <div className="tour-details">
          <div className="detail-grid">
            <div className="detail-item">
              <strong>Dates:</strong> {currentTour.dates}
            </div>
            <div className="detail-item">
              <strong>Distance:</strong> {currentTour.distance_nm} nautical miles
            </div>
            <div className="detail-item">
              <strong>Stops:</strong> {currentTour.stops}
            </div>
            <div className="detail-item">
              <strong>Focus:</strong> {currentTour.focus}
            </div>
            {selectedYear === '1925' && (
              <>
                <div className="detail-item">
                  <strong>Aircraft:</strong> {(currentTour as Tour1925).aircraft}
                </div>
                <div className="detail-item">
                  <strong>Sponsors:</strong> {(currentTour as Tour1925).sponsors}
                </div>
              </>
            )}
            {selectedYear === '2025' && (
              <div className="detail-item">
                <strong>Overnight Stops:</strong> {(currentTour as Tour2025).overnight_stops.join(', ')}
              </div>
            )}
          </div>
        </div>

        <div className="notable-aircraft">
          <h3>Notable Aircraft</h3>
          <div className="aircraft-list">
            {selectedYear === '1925' 
              ? (currentTour as Tour1925).notable_aircraft.map((aircraft: string, index: number) => (
                  <span key={index} className="aircraft-tag">{aircraft}</span>
                ))
              : (currentTour as Tour2025).notable_aircraft.map((aircraft: string, index: number) => (
                  <span key={index} className="aircraft-tag">{aircraft}</span>
                ))
            }
          </div>
        </div>

        <div className="itinerary-timeline">
          {currentTour.itinerary.map((leg, index) => (
            <div key={index} className="timeline-item">
              <div className="timeline-marker">
                <div className="marker-dot"></div>
                {index < currentTour.itinerary.length - 1 && <div className="marker-line"></div>}
              </div>
              
              <div className="timeline-content">
                <div className="timeline-header">
                  <h3 className="timeline-date">{leg.date}</h3>
                  <h2 className="timeline-route">
                    {leg.departure.city} → {leg.arrival.city}
                  </h2>
                  <div className="timeline-airports">
                    <div className="airport-info">
                      <strong>From:</strong> {leg.departure.airport} {leg.departure.code ? `(${leg.departure.code})` : ''}
                    </div>
                    <div className="airport-info">
                      <strong>To:</strong> {leg.arrival.airport} {leg.arrival.code ? `(${leg.arrival.code})` : ''}
                    </div>
                    {leg.overnight && <div className="overnight-indicator">Overnight Stop</div>}
                  </div>
                </div>
                
                <div className="timeline-distance">
                  <strong>Distance:</strong> {leg.distance_nm} nautical miles
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Mobile Toggle */}
      <div className="mobile-year-toggle">
        <button 
          className={`toggle-btn ${selectedYear === '1925' ? 'active' : ''}`}
          onClick={() => setSelectedYear('1925')}
        >
          1925
        </button>
        <button 
          className={`toggle-btn ${selectedYear === '2025' ? 'active' : ''}`}
          onClick={() => setSelectedYear('2025')}
        >
          2025
        </button>
      </div>
    </div>
  )
}

export default Itinerary